
/* eslint-disable max-len */
import React, { useCallback, useState } from 'react';
import Loader from 'react-loader-spinner';
import { useEffectOnce } from 'react-use';
import { waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { Communicator } from '../../../concepts/objects';
import { Button, Colors, remInPx } from '../../../ui';
import { useResolve } from '../../Container';
import { PassportModel, PermissionCode } from '../../PassportModel';
import { ButtonStrip, Copy, Flexer } from '../../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../../popups/Popup';
import { AskToJoinView } from './AskToJoinView';

type JoinViewProps = { boardId: string, callback: (result: 'allow' | 'refuse', reason?: string) => void };
const JoinView: LFC<JoinViewProps> = ({ boardId, callback }) => {

    const api = useResolve(ApiClient);

    useEffectOnce(() => {
        waitFor(500)
            .then(() => api.exec(v2.boards.join(boardId, true)))
            .then(({ joined, reason }) => {
                callback(joined ? 'allow' : 'refuse', reason);
            })
            .catch(() => callback('refuse'));
    });

    return <Flexer flex grow center>
        <Flexer flex="column" padding="1" width="32">
            <Flexer flex="row" center>
                <Loader
                    type="TailSpin"
                    color={Colors.text}
                    height={remInPx(2)}
                    width={remInPx(2)}
                />
                <Flexer padding="0 1">
                    <Copy flush size="1.5">Joining board...</Copy>
                </Flexer>
            </Flexer>
        </Flexer>
    </Flexer>;
};

type LimitViewProps = { callback: () => void };
const LimitView: LFC<LimitViewProps> = ({ callback }) => {

    return <Flexer flex grow center>
        <Flexer flex="column" padding="1" width="32">
            <Flexer flex="row" center>
                <Flexer padding="0 1">
                    <Copy size="1.5">Sorry, there are no guest slots available.</Copy>
                    <Copy>If you wish to join the board owner will need to remove someone to create a free slot or invite you to join the team as a full member.</Copy>
                    <ButtonStrip align="center">
                        <Button variant="primary" size="lg" onAction={callback}>Okay</Button>
                    </ButtonStrip>
                </Flexer>
            </Flexer>
        </Flexer>
    </Flexer>;
};

export const joinPopup = (boardId: string) => ({ ui: JoinPopup, args: { boardId } });

export const JoinPopup: LFC<PopupProps> = ({ action, dismiss, args }) => {
    const { boardId } = args;

    const api = useResolve(ApiClient);
    const comms = useResolve(Communicator);
    const passport = useResolve(PassportModel);
    const [step, setStep] = useState<'try' | 'ask' | 'limit'>('try');

    const onAllow = useCallback(async () => {
        // TODO: Refactor this because setting model here seems wrong but dont have time now to improve
        const permissions = await api.exec(v2.me.permissions(['board', boardId]));
        passport.mutate(ps => {
            ps.clear();
            for (const code of permissions) {
                ps.add(code as PermissionCode);
            }
        });
        comms.send('arrive', { a: 1 });
        action(true);
    }, [action]);

    const onTryResult = useCallback((result: 'allow' | 'refuse', reason?: string) => {
        if (result === 'allow') {
            onAllow();
        }
        else if (reason === 'user_not_member') {
            window.location.href = `/boards/${boardId}/join`;
        }
        else if (reason === 'guest_limit_reached') {
            setStep('limit');
        }
        else {
            setStep('ask');
        }
    }, [onAllow, dismiss]);

    const onAskResult = useCallback((result: 'allow' | 'refuse') => {
        if (result === 'allow') {
            onAllow();
        }
        else {
            dismiss();
        }
    }, [onAllow, dismiss]);

    return <Popup>
        <Popup.Content>
            <Flexer flex center height="16">
                {step === 'try' && <JoinView boardId={boardId} callback={onTryResult} />}
                {step === 'ask' && <AskToJoinView autoAsk boardId={boardId} callback={onAskResult} />}
                {step === 'limit' && <LimitView callback={dismiss} />}
            </Flexer>
        </Popup.Content>
    </Popup>;
};
